<template>
  <div class="min-h-screen pb-20">
    <!-- Header -->
    <header class="bg-white shadow-sm sticky top-0 z-10">
      <div class="max-w-7xl mx-auto px-4 py-4">
        <div class="flex items-center gap-3 mb-2">
          <button
            @click="$router.push('/lists')"
            class="text-gray-600 hover:text-gray-900"
          >
            {{ $t('listDetail.back') }}
          </button>
          <h1 class="text-xl font-bold text-gray-900 flex-1">{{ list?.name }}</h1>
          <button
            v-if="list?.is_owner"
            @click="openInviteModal"
            class="text-gray-600 hover:text-gray-900 text-xl"
            :title="$t('listDetail.manageList')"
          >
            ⚙️
          </button>
          <UserMenu />
        </div>
        <div v-if="list" class="text-sm text-gray-600 flex items-center gap-4">
          <span>{{ $t('lists.items', { count: items.length }, items.length) }}</span>
        </div>
      </div>
    </header>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
    </div>

    <!-- Content -->
    <main v-else class="max-w-7xl mx-auto px-4 py-6">
      <!-- Empty State -->
      <div v-if="items.length === 0" class="text-center py-12">
        <div class="text-6xl mb-4">📦</div>
        <h2 class="text-xl font-semibold text-gray-900 mb-2">{{ $t('listDetail.noItemsYet') }}</h2>
        <p class="text-gray-600">{{ $t('listDetail.noItemsDescription') }}</p>
      </div>

      <!-- Items List -->
      <draggable
        v-else
        v-model="items"
        item-key="id"
        handle=".drag-handle"
        :animation="150"
        @end="persistOrder"
        class="space-y-2"
      >
        <template #item="{ element: item }">
          <div
            class="card flex items-center gap-3"
            :class="{ 'opacity-60': item.is_completed }"
          >
            <!-- Drag Handle -->
            <span
              class="drag-handle cursor-grab text-gray-400 select-none touch-none px-1"
              :title="$t('listDetail.dragToReorder')"
            >
              ⠿
            </span>

            <!-- Checkbox -->
            <input
              type="checkbox"
              :checked="item.is_completed"
              @change="toggleItem(item)"
              class="w-5 h-5 text-primary-500 rounded focus:ring-2 focus:ring-primary-500"
            />

            <!-- Item Content -->
            <div class="flex-1 min-w-0 cursor-pointer" @click="openEditModal(item)">
              <div class="flex items-baseline gap-2">
                <h3
                  class="font-medium text-gray-900"
                  :class="{ 'line-through': item.is_completed }"
                >
                  {{ item.name }}
                </h3>
                <span v-if="item.quantity" class="text-sm text-gray-500">
                  × {{ item.quantity }}
                </span>
              </div>
              <p v-if="item.notes" class="text-sm text-gray-600 mt-1">
                {{ item.notes }}
              </p>
            </div>

            <!-- Delete Button -->
            <button
              @click="deleteItem(item)"
              class="text-red-500 hover:text-red-700 p-2"
              :title="$t('listDetail.deleteItem')"
            >
              🗑️
            </button>
          </div>
        </template>
      </draggable>
    </main>

    <!-- FAB - Add Item -->
    <button @click="openAddModal" class="fab">
      +
    </button>

    <!-- Add Item Modal -->
    <div
      v-if="showAddModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50"
      @click.self="closeAddModal"
    >
      <div class="bg-white rounded-t-xl sm:rounded-xl p-6 max-w-md w-full">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-xl font-bold">{{ editingItem ? $t('common.edit') : $t('listDetail.addItem') }}</h2>
          <button
            type="button"
            @click="handleAddItem"
            :disabled="adding || !newItem.name"
            class="bg-primary-500 hover:bg-primary-600 disabled:opacity-30 text-white text-2xl font-bold leading-none w-9 h-9 rounded-full flex items-center justify-center"
            :title="editingItem ? $t('common.save') : $t('common.add')"
          >
            +
          </button>
        </div>
        <form @submit.prevent="handleAddItem" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t('listDetail.itemNameRequired') }}
            </label>
            <input
              v-model="newItem.name"
              type="text"
              class="input"
              :placeholder="$t('listDetail.itemNamePlaceholder')"
              required
              ref="nameInput"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t('common.quantity') }}
            </label>
            <input
              v-model="newItem.quantity"
              type="text"
              class="input"
              :placeholder="$t('listDetail.quantityPlaceholder')"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t('common.notes') }}
            </label>
            <textarea
              v-model="newItem.notes"
              class="input"
              :placeholder="$t('listDetail.notesPlaceholder')"
              rows="2"
            ></textarea>
          </div>

          <div class="flex gap-3">
            <button
              type="button"
              @click="closeAddModal"
              class="btn btn-secondary flex-1"
            >
              {{ $t('common.cancel') }}
            </button>
            <button
              type="submit"
              class="btn btn-primary flex-1"
              :disabled="adding"
            >
              {{ adding ? $t('listDetail.adding') : editingItem ? $t('common.save') : $t('common.add') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Invite User Modal -->
    <div
      v-if="showInviteModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      @click.self="closeInviteModal"
    >
      <div class="bg-white rounded-xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <h2 class="text-xl font-bold mb-4">{{ $t('listDetail.listSettings') }}</h2>

        <div v-if="inviteSuccess" class="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
          {{ $t('listDetail.invitationSent') }}
        </div>
        <div v-if="inviteError" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {{ inviteError }}
        </div>

        <!-- Invited Users List -->
        <div v-if="list?.is_owner" class="mb-6">
          <h3 class="text-sm font-semibold text-gray-900 mb-2">{{ $t('listDetail.invitedUsers') }}</h3>

          <div v-if="loadingInvitations" class="text-center py-4">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto"></div>
          </div>

          <div v-else-if="invitedUsers.length === 0" class="text-sm text-gray-500 py-3">
            {{ $t('listDetail.noInvitedUsers') }}
          </div>

          <div v-else class="space-y-2">
            <div
              v-for="invitation in invitedUsers"
              :key="invitation.id"
              class="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div class="flex-1">
                <p class="text-sm font-medium text-gray-900">{{ invitation.invitee_email }}</p>
                <p class="text-xs text-gray-500">
                  {{ invitation.can_edit ? $t('listDetail.canEdit') : $t('listDetail.viewOnly') }}
                </p>
              </div>
              <button
                @click="handleRemoveInvitation(invitation)"
                :disabled="removingInvitation === invitation.id"
                class="text-red-500 hover:text-red-700 p-2 disabled:opacity-50"
                :title="$t('listDetail.removeInvitation')"
              >
                🗑️
              </button>
            </div>
          </div>

          <hr class="my-4" />
        </div>

        <!-- Invite Form (only for owners) -->
        <form v-if="list?.is_owner" @submit.prevent="handleInviteUser" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t('listDetail.emailAddressRequired') }}
            </label>
            <input
              v-model="inviteEmail"
              type="email"
              class="input"
              :placeholder="$t('listDetail.emailPlaceholder')"
              required
            />
          </div>

          <div class="flex items-center">
            <input
              v-model="inviteCanEdit"
              type="checkbox"
              id="canEdit"
              class="w-4 h-4 text-primary-500 rounded focus:ring-2 focus:ring-primary-500"
            />
            <label for="canEdit" class="ml-2 text-sm text-gray-700">
              {{ $t('listDetail.canEdit') }}
            </label>
          </div>

          <div class="flex gap-3">
            <button
              type="button"
              @click="closeInviteModal"
              class="btn btn-secondary flex-1"
            >
              {{ $t('common.cancel') }}
            </button>
            <button
              type="submit"
              class="btn btn-primary flex-1"
              :disabled="inviting"
            >
              {{ inviting ? $t('listDetail.inviting') : $t('listDetail.sendInvite') }}
            </button>
          </div>
        </form>

        <!-- Delete List Section (only for owners) -->
        <div v-if="list?.is_owner">
          <hr class="my-6" />

          <div class="space-y-3">
            <h3 class="text-sm font-semibold text-gray-900">{{ $t('listDetail.deleteList') }}</h3>
            <p class="text-sm text-gray-600">
              {{ $t('listDetail.deleteListDescription') }}
            </p>
            <button
              @click="handleDeleteList"
              :disabled="deleting"
              class="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ deleting ? $t('listDetail.deleting') : $t('listDetail.deleteList') }}
            </button>
          </div>
        </div>

        <!-- Close button for non-owners -->
        <div v-if="!list?.is_owner" class="flex justify-end">
          <button
            type="button"
            @click="closeInviteModal"
            class="btn btn-secondary"
          >
            {{ $t('common.close') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import draggable from 'vuedraggable'
import apiClient from '../api/client'
import UserMenu from '../components/UserMenu.vue'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const listId = computed(() => route.params.id)

const list = ref(null)
const items = ref([])
const loading = ref(true)

const showAddModal = ref(false)
const newItem = ref({ name: '', quantity: '', notes: '' })
const editingItem = ref(null)
const adding = ref(false)
const nameInput = ref(null)

const showInviteModal = ref(false)
const inviteEmail = ref('')
const inviteCanEdit = ref(true)
const inviting = ref(false)
const inviteSuccess = ref(false)
const inviteError = ref('')
const invitedUsers = ref([])
const loadingInvitations = ref(false)
const removingInvitation = ref(null)
const deleting = ref(false)

const persistOrder = async () => {
  try {
    await apiClient.put(`/api/lists/${listId.value}/items/reorder`, {
      item_ids: items.value.map((i) => i.id),
    })
  } catch (error) {
    console.error('Failed to save order:', error)
    await fetchItems() // re-sync to the server order on failure
  }
}

const fetchList = async () => {
  try {
    const response = await apiClient.get(`/api/lists/${listId.value}`)
    list.value = response.data
  } catch (error) {
    console.error('Failed to fetch list:', error)
  }
}

const fetchItems = async () => {
  try {
    const response = await apiClient.get(`/api/lists/${listId.value}/items`)
    items.value = response.data
  } catch (error) {
    console.error('Failed to fetch items:', error)
  } finally {
    loading.value = false
  }
}

const openAddModal = async () => {
  showAddModal.value = true
  await nextTick()
  nameInput.value?.focus()
}

const closeAddModal = () => {
  showAddModal.value = false
  newItem.value = { name: '', quantity: '', notes: '' }
  editingItem.value = null
}

const openEditModal = async (item) => {
  editingItem.value = item
  newItem.value = { name: item.name, quantity: item.quantity ?? '', notes: item.notes ?? '' }
  showAddModal.value = true
  await nextTick()
  nameInput.value?.focus()
}

const handleAddItem = async () => {
  adding.value = true
  try {
    const payload = {
      name: newItem.value.name,
      notes: newItem.value.notes || '',
      quantity: newItem.value.quantity || null,
    }

    if (editingItem.value) {
      const response = await apiClient.put(
        `/api/lists/${listId.value}/items/${editingItem.value.id}`,
        payload
      )
      Object.assign(editingItem.value, response.data)
    } else {
      const response = await apiClient.post(
        `/api/lists/${listId.value}/items`,
        payload
      )
      items.value.unshift(response.data)
    }
    closeAddModal()
  } catch (error) {
    console.error('Failed to save item:', error)
    alert(t('listDetail.failedToAdd'))
  } finally {
    adding.value = false
  }
}

const toggleItem = async (item) => {
  try {
    const response = await apiClient.put(
      `/api/lists/${listId.value}/items/${item.id}`,
      { is_completed: !item.is_completed }
    )
    Object.assign(item, response.data)
  } catch (error) {
    console.error('Failed to toggle item:', error)
  }
}

const deleteItem = async (item) => {
  try {
    await apiClient.delete(`/api/lists/${listId.value}/items/${item.id}`)
    items.value = items.value.filter((i) => i.id !== item.id)
  } catch (error) {
    console.error('Failed to delete item:', error)
    alert(t('listDetail.failedToDelete'))
  }
}

const fetchInvitations = async () => {
  if (!list.value?.is_owner) return

  loadingInvitations.value = true
  try {
    const response = await apiClient.get(`/api/lists/${listId.value}/invitations`)
    invitedUsers.value = response.data
  } catch (error) {
    console.error('Failed to fetch invitations:', error)
  } finally {
    loadingInvitations.value = false
  }
}

const openInviteModal = async () => {
  showInviteModal.value = true
  await fetchInvitations()
}

const closeInviteModal = () => {
  showInviteModal.value = false
  inviteEmail.value = ''
  inviteCanEdit.value = true
  inviteSuccess.value = false
  inviteError.value = ''
  invitedUsers.value = []
}

const handleInviteUser = async () => {
  inviting.value = true
  inviteSuccess.value = false
  inviteError.value = ''

  try {
    await apiClient.post(`/api/lists/${listId.value}/invite`, {
      invitee_email: inviteEmail.value,
      can_edit: inviteCanEdit.value,
    })
    inviteSuccess.value = true
    inviteEmail.value = ''

    await fetchInvitations()

    setTimeout(() => {
      inviteSuccess.value = false
    }, 2000)
  } catch (error) {
    console.error('Failed to invite user:', error)
    inviteError.value = error.response?.data?.error || t('listDetail.failedToInvite')
  } finally {
    inviting.value = false
  }
}

const handleRemoveInvitation = async (invitation) => {
  if (!confirm(t('listDetail.confirmRemoveInvitation', { email: invitation.invitee_email }))) return

  removingInvitation.value = invitation.id
  try {
    await apiClient.delete(`/api/lists/${listId.value}/invitations/${invitation.id}`)
    invitedUsers.value = invitedUsers.value.filter((i) => i.id !== invitation.id)
  } catch (error) {
    console.error('Failed to remove invitation:', error)
    alert(t('listDetail.failedToRemoveInvitation'))
  } finally {
    removingInvitation.value = null
  }
}

const handleDeleteList = async () => {
  if (!confirm(t('listDetail.deleteListConfirm'))) return

  deleting.value = true
  try {
    await apiClient.delete(`/api/lists/${listId.value}`)
    router.push('/lists')
  } catch (error) {
    console.error('Failed to delete list:', error)
    alert(t('listDetail.failedToDeleteList'))
  } finally {
    deleting.value = false
  }
}

onMounted(async () => {
  await Promise.all([fetchList(), fetchItems()])
})
</script>
